import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import {
    SlotPriorityUpdate,
    ZonePriorityUpdate,
} from '@control-tower/platform-wms';
import { WarehouseStructure } from '../../entities/warehouse.dto';
import { WarehouseDataService } from '../warehouses/warehouse.data.service';
import { ZoneFacade } from '../../application/zone.facade';
import { ZoneDto } from '../../entities/zone.dto';
import { LoggerService } from '@pf/shared-services';

@Injectable()
export class WarehouseStructureService {
    private readonly warehouseDataService = inject(WarehouseDataService);
    private readonly zoneFacade = inject(ZoneFacade);
    private readonly logger = inject(LoggerService);

    getStructure$(id: string): Observable<WarehouseStructure> {
        return this.warehouseDataService.getStructure$(id).pipe(
            tap(structure => {
                this.zoneFacade.setNodes(structure);
            }),
            switchMap(structure => {
                return this.ensureZonePrioritiesAreSet$(id, structure);
            })
        );
    }

    updateStructure$(
        id: string,
        zones: ZonePriorityUpdate[],
        slots: SlotPriorityUpdate[]
    ) {
        return this.warehouseDataService.updateStructure$(id, zones, slots);
    }

    updateZonePriorities$(
        warehouseId: string,
        zones: ZoneDto[]
    ): Observable<WarehouseStructure | null> {
        if (zones.length === 0) {
            return of(null);
        }

        let nextZonePriority = zones[0].zonePriority || 1;
        const updatedZones = zones.reduce<ZonePriorityUpdate[]>((acc, zone) => {
            if (!zone.zonePriority || zone.zonePriority < nextZonePriority) {
                acc.push({
                    id: zone.id,
                    priority: nextZonePriority,
                });
                nextZonePriority++;
            } else {
                nextZonePriority = zone.zonePriority + 1;
            }
            return acc;
        }, []);

        return updatedZones.length > 0
            ? this.updateStructure$(warehouseId, updatedZones, [])
            : of(null);
    }

    reorderZonePriorities(
        zoneIdToMove: string,
        zoneIdToMoveBefore: string
    ): Observable<WarehouseStructure | null> {
        const zoneToMove = this.zoneFacade.getById(zoneIdToMove);
        const zoneToMoveBefore = this.zoneFacade.getById(zoneIdToMoveBefore);

        if (!zoneToMove || !zoneToMoveBefore) {
            this.logger.error('Invalid move. Zone information is not set!');
            return of(null);
        }

        if (zoneToMove.warehouseId !== zoneToMoveBefore.warehouseId) {
            this.logger.error(
                'Invalid move. Zones are not in the same warehouse!'
            );
            return of(null);
        }

        const zoneToMoveType = this.zoneFacade.getZoneTypeOfZone(zoneToMove);
        if (!zoneToMoveType) {
            this.logger.error(
                'Invalid move. Zone type information is misconfigured!'
            );
            return of(null);
        }

        const zonesToMoveUpInPriority =
            this.zoneFacade.getZonesWithMatchingLocationIdentifiers(
                zoneToMove.warehouseId,
                zoneToMove.locationIdentifiers
            );
        const zonesToMoveBefore =
            this.zoneFacade.getZonesWithMatchingLocationIdentifiers(
                zoneToMoveBefore.warehouseId,
                zoneToMoveBefore.locationIdentifiers
            );

        const allZones = this.zoneFacade.getZonesByPriority(
            zoneToMove.warehouseId
        );

        if (
            !zonesToMoveUpInPriority.length ||
            !zonesToMoveBefore.length ||
            !allZones.length
        ) {
            this.logger.error('Invalid move. Cannot find zones to move.');
            return of(null);
        }

        const updatedZones = this.reorderZones(
            allZones,
            zonesToMoveBefore,
            zonesToMoveUpInPriority
        );

        if (allZones.length !== updatedZones.length) {
            this.logger.error('Invalid move. Ordering logic is incorrect.');
            return of(null);
        }

        return this.updateZonePriorities$(zoneToMove.warehouseId, updatedZones);
    }

    private reorderZones(
        allZones: ZoneDto[],
        zonesToMoveBefore: ZoneDto[],
        zonesToMoveUpInPriority: ZoneDto[]
    ) {
        // Create new list of all zones with the zones reordered. Then we pass that list to the warehouse facade updateZonePriorities$
        const indexOfZoneToMoveBefore = allZones.findIndex(
            z => z.id === zonesToMoveBefore[0].id
        );
        const indexOfZoneToMove = allZones.findIndex(
            z => z.id === zonesToMoveUpInPriority[0].id
        );

        const updatedZones = [...allZones];

        updatedZones.splice(indexOfZoneToMove, zonesToMoveUpInPriority.length);
        updatedZones.splice(
            indexOfZoneToMoveBefore,
            0,
            ...zonesToMoveUpInPriority
        );
        return updatedZones;
    }

    private ensureZonePrioritiesAreSet$(
        warehouseId: string,
        structure: WarehouseStructure
    ): Observable<WarehouseStructure> {
        const zones = this.zoneFacade.getZonesByPriority(warehouseId);

        return this.updateZonePriorities$(warehouseId, zones).pipe(
            tap(updatedStructure => {
                this.zoneFacade.setNodes(updatedStructure);
            }),
            map(updatedStructure => updatedStructure || structure)
        );
    }
}
